import { safeLocalStorage } from "@/lib/storage/safe-storage";
import { track } from "./tracker";
import {
  createOnboardingFlow,
  deserializeOnboardingFlow,
  getNowIsoString,
  normalizeAccountType,
  normalizeEntityType,
  ONBOARDING_FLOW_COOKIE_NAME,
  serializeOnboardingFlow,
  type OnboardingFlowState,
  type SetupStep,
} from "./onboarding-flow-state";

const ONBOARDING_FLOW_STORAGE_KEY = "analytics:onboarding-flow";
const activationWindowMilliseconds = 14 * 24 * 60 * 60 * 1000;

interface StepMetadata {
  step_name: "account_type" | "company_details" | "site_details";
  step_category: "identity" | "company";
}

interface SetupContext {
  account_type?: string | null;
  entity_type?: string | null;
  company_id?: string | null;
}

interface SetupStartContext extends SetupContext {
  existing_company?: boolean;
  has_prefill?: boolean;
  prefill_source?: string;
  return_to?: string;
  intent?: string;
}

interface SetupCompleteContext extends SetupContext {
  completion_path: string;
}

const setupStepMetadata: Record<SetupStep, StepMetadata> = {
  1: {
    step_name: "account_type",
    step_category: "identity",
  },
  2: {
    step_name: "company_details",
    step_category: "company",
  },
  3: {
    step_name: "site_details",
    step_category: "company",
  },
};

function readStoredFlow(): OnboardingFlowState | null {
  const serializedFlow = safeLocalStorage().getItem(
    ONBOARDING_FLOW_STORAGE_KEY,
  );
  if (!serializedFlow) return null;
  return deserializeOnboardingFlow(serializedFlow);
}

function writeStoredFlow(flow: OnboardingFlowState): OnboardingFlowState {
  safeLocalStorage().setItem(
    ONBOARDING_FLOW_STORAGE_KEY,
    serializeOnboardingFlow(flow),
  );
  return flow;
}

function mergeSetupContext(
  flow: OnboardingFlowState,
  context?: SetupContext,
): OnboardingFlowState {
  if (!context) return flow;

  const accountType = normalizeAccountType(context.account_type);
  const entityType = normalizeEntityType(context.entity_type);

  return {
    ...flow,
    account_type: accountType === "unknown" ? flow.account_type : accountType,
    entity_type: entityType === "unknown" ? flow.entity_type : entityType,
  };
}

function buildBaseProperties(flow: OnboardingFlowState) {
  return {
    flow_id: flow.flow_id,
    account_type: flow.account_type,
    entity_type: flow.entity_type,
    entry_surface: flow.entry_surface,
    return_to: flow.return_to,
    intent: flow.intent,
    product: flow.product,
    plan: flow.plan,
  };
}

function getDurationSeconds(
  startedAt?: string,
  endedAt?: string,
): number | undefined {
  if (!startedAt || !endedAt) return undefined;

  const startedAtTimestamp = Date.parse(startedAt);
  const endedAtTimestamp = Date.parse(endedAt);

  if (Number.isNaN(startedAtTimestamp) || Number.isNaN(endedAtTimestamp)) {
    return undefined;
  }

  return Math.max(
    0,
    Math.round((endedAtTimestamp - startedAtTimestamp) / 1000),
  );
}

function shouldTrackActivation(flow: OnboardingFlowState): boolean {
  if (!flow.setup_completed_at || flow.activation_completed_at) return false;

  const setupCompletedAt = Date.parse(flow.setup_completed_at);
  if (Number.isNaN(setupCompletedAt)) return false;

  return Date.now() - setupCompletedAt <= activationWindowMilliseconds;
}

function getCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;

  const cookie = document.cookie
    .split("; ")
    .find((cookieEntry) => cookieEntry.startsWith(`${name}=`));

  if (!cookie) return null;
  const rawValue = cookie.slice(name.length + 1);
  try {
    return decodeURIComponent(rawValue);
  } catch {
    return null;
  }
}

function deleteCookie(name: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
}

export function hydrateOnboardingFlowFromCookie(): OnboardingFlowState | null {
  const cookieValue = getCookieValue(ONBOARDING_FLOW_COOKIE_NAME);
  if (!cookieValue) return readStoredFlow();

  const flow = deserializeOnboardingFlow(cookieValue);
  if (!flow) {
    deleteCookie(ONBOARDING_FLOW_COOKIE_NAME);
    return readStoredFlow();
  }

  deleteCookie(ONBOARDING_FLOW_COOKIE_NAME);
  return writeStoredFlow(flow);
}

export function hydrateOnboardingFlowOnSignupCallback(): OnboardingFlowState | null {
  const flow = hydrateOnboardingFlowFromCookie();
  if (!flow || flow.signup_completed_at) return flow;

  return writeStoredFlow({
    ...flow,
    signup_completed_at: getNowIsoString(),
  });
}

function flowForSetupSession(context?: SetupStartContext): OnboardingFlowState {
  const hydrated = hydrateOnboardingFlowFromCookie();
  if (hydrated) return hydrated;
  return writeStoredFlow(
    createOnboardingFlow({
      entry_surface:
        typeof document === "undefined" || !document.referrer
          ? "setup_direct"
          : document.referrer,
      return_to: context?.return_to,
      intent: context?.intent,
      signup_completed_at: getNowIsoString(),
    }),
  );
}

export function trackSetupStarted(context?: SetupStartContext): void {
  const flow = flowForSetupSession(context);
  if (flow.setup_started_at) return;

  const nextFlow = writeStoredFlow({
    ...mergeSetupContext(flow, context),
    setup_started_at: getNowIsoString(),
  });

  track("setup.started", {
    ...buildBaseProperties(nextFlow),
    has_prefill: context?.has_prefill === true,
    prefill_source: context?.prefill_source ?? nextFlow.claim_context,
    existing_company: context?.existing_company === true,
  });
}

export function trackSetupStepViewed(
  step: SetupStep,
  context?: SetupContext,
): void {
  const flow = hydrateOnboardingFlowFromCookie();
  if (!flow || flow.viewed_steps.includes(step)) return;

  const nextFlow = writeStoredFlow({
    ...mergeSetupContext(flow, context),
    viewed_steps: [...flow.viewed_steps, step],
  });
  const metadata = setupStepMetadata[step];

  track("setup.step_viewed", {
    ...buildBaseProperties(nextFlow),
    step,
    step_name: metadata.step_name,
    step_category: metadata.step_category,
    company_id: context?.company_id ?? undefined,
  });
}

export function trackSetupStepValidationFailed(
  step: SetupStep,
  details: { firstFailingField?: string; errorCount?: number },
  context?: SetupContext,
): void {
  const flow = hydrateOnboardingFlowFromCookie();
  if (!flow) return;
  const metadata = setupStepMetadata[step];
  const base = mergeSetupContext(flow, context);

  track("setup.step_validation_failed", {
    flow_id: base.flow_id,
    step,
    step_name: metadata.step_name,
    firstFailingField: details.firstFailingField,
    errorCount: details.errorCount,
  });
}

export function trackSetupStepCompleted(
  step: SetupStep,
  context?: SetupContext,
): void {
  const flow = hydrateOnboardingFlowFromCookie();
  if (!flow || flow.completed_steps.includes(step)) return;

  const nextFlow = writeStoredFlow({
    ...mergeSetupContext(flow, context),
    completed_steps: [...flow.completed_steps, step],
  });
  const metadata = setupStepMetadata[step];

  track("setup.step_completed", {
    ...buildBaseProperties(nextFlow),
    step,
    step_name: metadata.step_name,
    step_category: metadata.step_category,
    company_id: context?.company_id ?? undefined,
  });
}

export function trackSetupCompleted(context: SetupCompleteContext): void {
  const flow = hydrateOnboardingFlowFromCookie();
  if (!flow || flow.setup_completed_at) return;

  const completedAt = getNowIsoString();
  const nextFlow = writeStoredFlow({
    ...mergeSetupContext(flow, context),
    setup_completed_at: completedAt,
  });

  track("setup.completed", {
    ...buildBaseProperties(nextFlow),
    company_id: context.company_id ?? undefined,
    completion_path: context.completion_path,
    duration_seconds: getDurationSeconds(
      nextFlow.setup_started_at ?? nextFlow.started_at,
      completedAt,
    ),
  });
}

export function trackActivationCompleted(
  activationEvent: string,
  context?: SetupContext,
): void {
  const flow = readStoredFlow() ?? hydrateOnboardingFlowFromCookie();
  if (!flow || !shouldTrackActivation(flow)) return;

  const activatedAt = getNowIsoString();
  const nextFlow = writeStoredFlow({
    ...mergeSetupContext(flow, context),
    activation_completed_at: activatedAt,
  });

  track("setup.activation_completed", {
    ...buildBaseProperties(nextFlow),
    activation_event: activationEvent,
    activation_window_hours: getDurationSeconds(
      nextFlow.setup_completed_at,
      activatedAt,
    )
      ? Math.round(
          (getDurationSeconds(nextFlow.setup_completed_at, activatedAt) ?? 0) /
            360,
        ) / 10
      : undefined,
  });
}
